// pages/features/CommunicationFeaturePage.jsx
import React from "react";
import { Link } from "react-router-dom";
import HomeHeader from "../modals/HomeHeader.js";

const EXPLORE_PATH = "/ProviderSearchPage";

export default function CommunicationFeaturePage() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <HomeHeader />

      {/* HERO */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
              Secure Communication
            </h1>

            <p className="mt-3 text-lg text-gray-600 leading-relaxed">
              Communicate directly and securely—without relying on external
              tools or exposing personal contact information.
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              <Pill label="Secure Messaging" />
              <Pill label="Patient ↔ Provider" accent="emerald" />
              <Pill label="Centralized" />
              <Pill label="Privacy-First" accent="teal" />
            </div>

            <div className="mt-8">
              <Link
                to={EXPLORE_PATH}
                className="inline-flex items-center justify-center bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-full px-6 py-3 text-sm transition-colors"
              >
                Explore how MedMaze works →
              </Link>
              <p className="mt-2 text-xs text-gray-500">
                Messaging stays within the MedMaze platform.
              </p>
            </div>
          </div>

          {/* Secure Chat Illustration */}
          <div className="rounded-2xl border-2 border-teal-400 bg-white shadow-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <span className="font-semibold text-gray-800">
                Secure Message Preview
              </span>
              <span className="text-xs text-teal-600 font-semibold">
                Encrypted
              </span>
            </div>

            <div className="px-5 py-5 space-y-3">
              <MessageBubble
                side="left"
                label="Patient"
                text="Hi Doctor, I wanted to ask a quick question before booking."
              />
              <MessageBubble
                side="right"
                label="Provider"
                text="Of course — happy to help. Let me know what you need."
              />
              <MessageBubble
                side="left"
                label="Patient"
                text="Do you accept BlueCross PPO for an initial consultation?"
              />
              <MessageBubble
                side="right"
                label="Provider"
                text="Yes, we do! You can book directly through your dashboard."
              />
            </div>
          </div>
        </div>
      </section>

      {/* COMMUNICATION WITHOUT FRICTION — full-width teal gradient */}
      <section className="bg-gradient-to-br from-teal-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900">
            Communication without friction
          </h2>
          <div className="mt-4 space-y-3 text-gray-700 leading-relaxed">
            <p>
              Patients and providers often rely on disconnected tools—emails,
              phone calls, or third-party apps—to communicate. MedMaze brings
              secure messaging directly into the platform, keeping conversations
              private, organized, and easy to manage.
            </p>
            <p>
              Messages stay tied to care context, reducing confusion and helping
              both sides communicate with clarity and confidence.
            </p>
          </div>
        </div>
      </section>

      {/* PATIENT + PROVIDER BENEFITS */}
      <section className="bg-white py-4 pb-16">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Patient panel */}
          <div className="rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-7 bg-gradient-to-br from-teal-50 to-white">
            <h3 className="text-xl font-bold text-gray-900">
              Clear communication for patients
            </h3>
            <ul className="mt-4 space-y-3 text-sm text-gray-700">
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  Ask questions directly before or after booking appointments.
                </span>
              </li>
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  Keep all provider conversations in one secure place.
                </span>
              </li>
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  Communicate without sharing personal contact details.
                </span>
              </li>
            </ul>
          </div>

          {/* Provider panel */}
          <div className="rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-7 bg-gradient-to-br from-teal-50 to-white">
            <h3 className="text-xl font-bold text-gray-900">
              Focused messaging for providers
            </h3>
            <ul className="mt-4 space-y-3 text-sm text-gray-700">
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  Respond to patients within a secure, centralized inbox.
                </span>
              </li>
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  Reduce reliance on phone calls and scattered communication.
                </span>
              </li>
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  Maintain professional boundaries while staying accessible.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="bg-teal-50 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 sm:p-8">
            <h3 className="text-lg font-bold text-gray-900">
              Why secure communication matters
            </h3>
            <p className="mt-2 text-sm text-gray-700">
              By keeping conversations secure and centralized, MedMaze enables
              better coordination, clearer expectations, and more confident
              care—without added complexity.
            </p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-white py-8 text-xs text-gray-500 text-center border-t">
        © {new Date().getFullYear()} MedMaze. All rights reserved.
      </footer>
    </div>
  );
}

function Pill({ label, accent }) {
  const base =
    "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold";
  const theme =
    accent === "emerald"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : accent === "teal"
        ? "border-teal-300 bg-teal-50 text-teal-700"
        : "border-teal-200 bg-teal-50 text-teal-700";

  return <span className={`${base} ${theme}`}>{label}</span>;
}

function MessageBubble({ side, label, text }) {
  const isRight = side === "right";
  return (
    <div className={`flex ${isRight ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[75%] rounded-xl px-4 py-2 text-sm ${
          isRight
            ? "bg-teal-100 text-teal-800"
            : "bg-gray-50 border border-gray-200 text-gray-700"
        }`}
      >
        <p className="text-xs font-semibold mb-1">{label}</p>
        {text}
      </div>
    </div>
  );
}

function BulletIcon() {
  return (
    <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700">
      ✓
    </span>
  );
}
